import React, { useState, useEffect } from 'react';
import { Map, Trash, Edit, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAreas, createArea, deleteArea } from '../../services/areaAPI';
import { getUsers } from '../../services/observationAPI'; // Import getUsers

import toast from 'react-hot-toast';

const AreaManagement = () => {
    const [areas, setAreas] = useState([]);
    const [users, setUsers] = useState([]); // State to store the list of users
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedArea, setSelectedArea] = useState(null);

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [hod, setHod] = useState(''); // State for Head of Department

    // Fetch areas and users
    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem("token");
                const [areasRes, usersRes] = await Promise.all([
                    getAreas(token),
                    getUsers() // Fetch users
                ]);
                setAreas(areasRes);
                setUsers(usersRes); // Set users state
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const fetchAreas = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await getAreas(token);
            setAreas(response);
        } catch (error) {
            console.error('Error fetching areas:', error);
            toast.error('Failed to load areas');
        }
    };

    const openAddModal = () => {
        setIsEditMode(false);
        setSelectedArea(null);
        resetForm();
        setIsModalOpen(true);
    };

    const openEditModal = (area) => {
        setIsEditMode(true);
        setSelectedArea(area);
        setName(area.name);
        setDescription(area.description);
        setHod(area.hod || ''); // Set HOD if available in the area data
        setIsModalOpen(true);
    };

    const resetForm = () => {
        setName('');
        setDescription('');
        setHod(''); // Reset HOD state
    };

    const closeModal = () => {
        setIsModalOpen(false);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const areaData = {
                name,
                description,
                hod // Include HOD in the data
            };

            if (isEditMode && selectedArea) {
                // Update existing area (assuming createArea can also handle updates or you have an updateArea function)
                // Note: Your original code calls createArea for both add and edit.
                // You might need a separate updateArea API call.
                // For now, I'm keeping the structure similar but passing the ID if needed by your API.
                 // If your API requires the ID for update, modify createArea or use a dedicated updateArea function
                 // Example if createArea handles update based on ID:
                 // const response = await createArea({ ...areaData, _id: selectedArea._id }, token);

                 // Assuming you need a separate update API call:
                 // Replace the following line with your actual update API call
                 console.log("Simulating update for area:", selectedArea._id, areaData);
                 toast('Update functionality not fully implemented in this example.');
                 closeModal(); // Close modal after simulated update
                 // After a real update, you would refetch areas:
                 // fetchAreas();


            } else {
                // Create new area
                const response = await createArea(areaData, token);

                // Add new area to state
                fetchAreas(); // Refetch areas to get the latest list
                setAreas(prev => [...prev, response]);

                toast.success('Area created successfully');
                closeModal();
            }

        } catch (error) {
            console.error('Error saving area:', error);
            toast.error(error.response?.data?.message || 'Failed to save area');
        }
        finally {
            setLoading(false);
        }
    };

    const handleDeleteArea = async (areaId) => {
        if (!window.confirm('Are you sure you want to delete this area?')) {
            return;
        }

        try {
            const token = localStorage.getItem("token");
            await deleteArea(areaId, token);

            // Remove area from state
            setAreas(prev => prev.filter(area => area._id !== areaId));

            toast.success('Area deleted successfully');
        } catch (error) {
            console.error('Error deleting area:', error);
            toast.error('Failed to delete area');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Area Management</h1>
                    <p className="text-gray-600">Manage areas for observation tracking.</p>
                </div>
                <button
                    onClick={openAddModal}
                    className="btn btn-primary flex items-center"
                >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Area
                </button>
            </div>

            {/* Area List */}
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                    {areas.length === 0 ? (
                        <li className="px-6 py-4 text-center text-gray-500">
                            No areas found. Create your first area to start tracking observations.
                        </li>
                    ) : (
                        areas
                            .filter(area => area && area._id)
                            .map((area) => (
                                <li key={area._id} className="px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0">
                                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <Map className="h-6 w-6 text-blue-600" />
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{area?.name}</div>
                                                <div className="text-sm text-gray-500">{area?.description}</div>
                                                 {/* Display HOD if available */}
                                                {area.hod && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        HOD: {users.find(user => user._id === area.hod)?.firstName} {users.find(user => user._id === area.hod)?.lastName}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Link
                                                to={`/admin/area/observations/${area._id}`}
                                                className="btn btn-secondary btn-sm"
                                            >
                                                View Observations
                                            </Link>
                                            {/* Edit button (currently commented out in your code, uncomment if needed) */}
                                            {/* <button
                                                onClick={() => openEditModal(area)}
                                                className="text-gray-400 hover:text-gray-500"
                                            >
                                                <Edit className="h-5 w-5" />
                                            </button> */}
                                            <button
                                                onClick={() => handleDeleteArea(area._id)}
                                                className="text-gray-400 hover:text-red-500"
                                            >
                                                <Trash className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))
                    )}
                </ul>
            </div>

            {/* Add/Edit Area Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h3 className="text-lg font-medium text-gray-900">
                                {isEditMode ? 'Edit Area' : 'Add New Area'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="px-6 py-4">
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                        Area Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="input mt-1"
                                        placeholder="Production Line 1"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                        Description
                                    </label>
                                    <textarea
                                        id="description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="input mt-1"
                                        rows={3}
                                        placeholder="Brief description of the area"
                                    />
                                </div>

                                {/* HOD Selection */}
                                <div>
                                    <label htmlFor="hod" className="block text-sm font-medium text-gray-700">
                                        Head of Department
                                    </label>
                                    <select
                                        id="hod"
                                        value={hod}
                                        onChange={(e) => setHod(e.target.value)}
                                        className="input mt-1"
                                    >
                                        <option value="">Select HOD</option>
                                        {users.map((user) => (
                                            <option key={user._id} value={user._id}>
                                                {user.firstName} {user.lastName} ({user.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    loading={loading}
                                    disabled={loading}
                                >
                                    {isEditMode ? 'Update Area' : 'Add Area'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AreaManagement;
